import { Box, Typography } from "@mui/material";
import { Property, Track } from "../../types";
import InfoCell from "../../../common/components/ui-kit/info-cell";
import { format } from "date-fns";
import { useGetActiveListing } from "../../hooks/listings.hook";
import { ListingType } from "../../dtos";
import { PROPERTY_TRACKS } from "../../utils";

interface FacilityItem {
  key: string;
  label: string;
  prefix?: string;
  format?: (
    value?: string | number | boolean,
    row?: Property,
  ) => string | undefined;
}

const trackFacilities: FacilityItem[] = [
  { key: "unitId", label: "Unit ID" },
  {
    key: "unitArea",
    label: "Unit Area",
    prefix: " m²",
  },
  {
    key: "landArea",
    label: "Land Area",
    prefix: " m²",
  },
  {
    key: "gardenArea",
    label: "Garden Area",
    prefix: " m²",
  },
  {
    key: "numberOfBedrooms",
    label: "Bedrooms",
  },
  {
    key: "numberOfBathrooms",
    label: "Bathrooms",
  },
  {
    key: "numberOfFloors",
    label: "Floors",
  },
  {
    key: "floorNumber",
    label: "Floor Number",
  },
  {
    key: "deliveryDate",
    label: "Delivery Date",
    format: (date?: string | number | boolean, row?: Track["property"]) => {
      if (typeof date === "boolean") return "N/A";
      if (row?.readyToMove) {
        return "Ready to move";
      }
      if (!date) return undefined;
      const parsedDate = new Date(date);
      return format(parsedDate, "d MMM yyyy");
    },
  },
  {
    key: "lastInstallmentDate",
    label: "Last Installment Date",
    format: (date?: string | number | boolean) => {
      if (typeof date === "boolean") return "N/A";
      if (!date) return undefined;
      const parsedDate = new Date(date);
      return format(parsedDate, "d MMM yyyy");
    },
  },
  {
    key: "phase",
    label: "Phase",
  },
  {
    key: "roofArea",
    label: "Roof Area",
    prefix: " m²",
  },
  {
    key: "finishing",
    label: "Finishing",
  },
  {
    key: "isUnderConstruction",
    label: "Under Construction",
    format: (value?: string | number | boolean) => {
      if (typeof value !== "boolean") return "N/A";
      if (value === null || value === undefined) return "N/A";
      return value ? "Yes" : "No";
    },
  },
  {
    key: "hospitalityType",
    label: "Hospitality Type",
  },
];

export default function FacilitiesSection({
  property,
  trackTypeName,
}: {
  property: Property;
  trackTypeName?: string;
}) {
  const isHospitality = trackTypeName === PROPERTY_TRACKS.HOSPITALITY;
  const { data, isLoading } = useGetActiveListing({
    params: { propertyUid: property.uid, listingType: ListingType.RESALE },
  });
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      <Typography
        sx={(theme) => theme.nawy.typography.english.variants.body.xs.semibold}
      >
        Facilities
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "40px",
          justifyContent: "space-between",
        }}
      >
        {trackFacilities.map((facility) => {
          if (!facility.key) return null;
          if (isHospitality && facility.key === "isUnderConstruction") return null;

          const value = property[facility.key as keyof Property];
          const formattedValue =
            facility.format?.(
              value as string | number | boolean | undefined,
              property,
            ) ?? value;

          const title = facility.label;
          const description =
            formattedValue !== undefined && formattedValue !== null
              ? `${formattedValue}${facility.prefix ?? ""}`
              : "N/A";

          return (
            <InfoCell
              key={facility.key}
              title={title}
              description={description}
            />
          );
        })}
        <InfoCell
          key={"websiteListingId"}
          title={"Website listing ID"}
          description={data?.inventoryListingId}
          isLoading={isLoading}
        />
        <Box
          sx={{
            flexGrow: 1,
          }}
        />
      </Box>
    </Box>
  );
}
